<template>
  <v-card>
    <v-toolbar :dark="$vuetify.theme.dark" color="primary">
      <v-row>
        <v-col cols="6" class="text-left">
          <v-toolbar-title class="textwithbackground--text">
            {{ $t('Торговые сессии') }}
          </v-toolbar-title>
        </v-col>
        <v-col cols="6" class="text-right">
          <btn-icon-tip
              v-if="canAddPublicSession"
              icon="add_circle"
              icon-color="secondaryiconbtn"
              :tip="$t('Добавить открытую сессию')"
              @click="openAddSessionDialog(createPublicSessionType)" />
          <btn-icon-tip
              v-if="canAddPrivateSession"
              icon="add_circle_outline"
              icon-color="secondaryiconbtn"
              :tip="$t('Добавить закрытую сессию')"
              @click="openAddSessionDialog(createPrivateSessionType)" />
        </v-col>
      </v-row>
    </v-toolbar>

    <v-data-table
        :headers="headers1"
        :items="sessionItems"
        :items-per-page="itemsPerPage"
        item-key="sessionId"
        class="1"
        disable-sort-by
        single-expand
        :show-expand="isAdmin || isLotOwner(lot)"
        :loading="isLoading"
        :locale="systemLocale">
      <template v-slot:progress>
        <v-progress-linear :active="isLoading" color="loader" indeterminate />
      </template>
      <!-- eslint-disable-next-line vue/valid-v-slot -->
      <template v-slot:item.sessionNumber="{ item }">
        {{ item.sessionNumber }}
      </template>
      <!-- eslint-disable-next-line vue/valid-v-slot -->
      <template v-slot:item.dateStart="{ item }">
        {{ item.dateStart
          ? $moment(item.dateStart).format('DD.MM.YYYY HH:mm')
          : null }}
      </template>
      <!-- eslint-disable-next-line vue/valid-v-slot -->
      <template v-slot:item.dateEnd="{ item }">
        {{ item.dateEnd
          ? $moment(item.dateEnd).format('DD.MM.YYYY HH:mm')
          : null }}
      </template>
      <!-- eslint-disable-next-line vue/valid-v-slot -->
      <template v-slot:item.dateEndWithProlongation="{ item }">
        {{ item.dateEndWithProlongation
          ? $moment(item.dateEndWithProlongation).format('DD.MM.YYYY HH:mm')
          : null }}
      </template>
      <!-- eslint-disable-next-line vue/valid-v-slot -->
      <template v-slot:item.sessionType="{ item }">
        {{ item.sessionType ? $t(SESSION_TYPE_ENUM[item.sessionType]) : null }}
      </template>
      <!-- eslint-disable-next-line vue/valid-v-slot -->
      <template v-slot:item.sessionId="{ item }">
        <div>
          <btn-icon-tip
              :id="`viewBtn-${item.sessionId}`"
              :tip="$t('Просмотреть сессию')"
              icon="visibility"
              @click="openEditSessionDialog(item.sessionId, item.sessionType, true)" />
          <btn-icon-tip
              v-if=" lot.canEditSession &&
                item.isLast &&
                !readonly"
              :id="`editBtn-${item.sessionId}`"
              :tip="$t('Редактировать сессию')"
              icon="edit"
              @click="openEditSessionDialog(item.sessionId, item.sessionType, false )" />
          <btn-icon-tip
              v-if="lot.canEditSession &&
                item.isLast &&
                !readonly &&
                lot.lotStatus === LOT_STATUSES.DRAFT"
              :id="`deleteBtn-${item.sessionId}`"
              :tip="$t('Удалить сессию')"
              icon="delete_forever"
              @click="openConfirmationBeforDelete(item.sessionId)" />
        </div>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <v-row class="text-left py-0">
            <text-array-view
                :display="item.invitedOrganizations.map(io => io.organizationName)"
                :label="$t('Приглашенные участники')" />
          </v-row>
        </td>
      </template>
    </v-data-table>
    <confirm-dialog
        v-if="confirmDialog.show"
        :dialog-prop="confirmDialog"
        @close="closeDialog('confirmDialog')" />
    <add-session-dialog
        v-if="addSessionDialog.show"
        :dialog-prop="addSessionDialog"
        @close="closeDialog('addSessionDialog')" />
    <edit-session-dialog
        v-if="editSessionDialog.show"
        :dialog-prop="editSessionDialog"
        @close="closeDialog('editSessionDialog')" />
  </v-card>
</template>

<script>

  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import TextArrayView from '@/components/reused/ui/TextArrayView.vue'
  import AddSessionDialog from './AddSessionDialog.vue'
  import EditSessionDialog from './EditSessionDialog.vue'
  import ConfirmDialog from '@/components/reused/common/ConfirmDialog.vue'
  import { SESSION_TYPE_ENUM, TRADE_DIRECTION_TYPE_ENUM } from '@/constants/enums'
  import { LOT_STATUSES, SESSION_TYPES, LOT_TYPES } from '@/constants/business'
  import { mapActions } from 'vuex'
  import { LOT_SESSIONS_MANAGEMENT } from '@/constants/store.js'

  export default {
    components: {
      BtnIconTip,
      TextArrayView,
      AddSessionDialog,
      EditSessionDialog,
      ConfirmDialog
    },
    props: {
      sessions: {
        type: Array,
        required: true
      },
      lot: {
        type: Object,
        required: true,
        default: () => {
          return {
            lotType: null,
            lotId: null,
            canCreateSession: false,
            canEditSession: false
          }
        }
      },
      positionsCount: {
        type: Number,
        required: true
      },
      loading: {
        type: Boolean,
        default: false
      },
      readonly: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        SESSION_TYPES,
        LOT_STATUSES,
        SESSION_TYPE_ENUM,
        TRADE_DIRECTION_TYPE_ENUM,
        isLoading: false,
        itemsPerPage: 5,
        addSessionDialog: {
          lot: null,
          sessionType: null,
          canViewStartPremiums: null,
          show: false,
          callback: null
        },
        confirmDialog: {
          show: false,
          warning: true,
          text: null,
          additional: null,
          callback: null
        },
        editSessionDialog: {
          sessionId: null,
          sessionType: null,
          lot: null,
          callback: null,
          canViewStartPremiums: null,
          show: false,
          readonly: false
        }
      }
    },
    computed: {
      headers1 () {
        let headers = [
          { text: this.$t('№ сессии'), sortable: false, value: 'sessionNumber' },
          { text: this.$t('Время начала'), sortable: false, value: 'dateStart' },
          { text: this.$t('Время завершения'), sortable: false, value: 'dateEnd' },
          { text: this.$t('Время завершения продленной сессии'), sortable: false, value: 'dateEndWithProlongation' },
          { text: this.$t('Тип сессии'), sortable: false, value: 'sessionType' },
          { text: this.$t('Действия'), sortable: false, value: 'sessionId' },
          { text: '', value: 'data-table-expand' }
        ]
        if (!this.isAdmin && !this.isLotOwner(this.lot)) {
          headers = headers.filter(h => h.value !== 'data-table-expand')
        }
        return headers
      },
      sessionCount () {
        return this.sessions.length || 0
      },
      sessionItems () {
        if (!this.sessions || this.sessions.length === 0) {
          return []
        }

        const newArray = this.sessions.map((s, index) => ({
          ...s,
          isLast: false
        }))
        newArray[this.sessions.length - 1].isLast = true
        return newArray
      },
      createPrivateSessionType () {
        return this.isPrivateRequestOffersLot
          ? SESSION_TYPES.PRIVATE_OFFERS
          : SESSION_TYPES.PRIVATE_TWO_STAGED_OFFERS
      },
      createPublicSessionType () {
        return this.isPublicRequestOffersLot
          ? SESSION_TYPES.PUBLIC_OFFERS
          : SESSION_TYPES.PUBLIC_TWO_STAGED_OFFERS
      },
      canViewStartPremiumsForCreating () {
        return this.sessionCount > 0
      },
      canViewStartPremiumsForEditing () {
        return this.sessionCount > 1
      },
      canAddPrivateSession () {
        return this.lot.canCreateSession &&
          (this.isPrivateRequestOffersLot ||
            (this.isTwoStagedLot && this.sessions.length === 0))
      },
      canAddPublicSession () {
        return this.lot.canCreateSession &&
          (this.isPublicRequestOffersLot || (this.isTwoStagedLot && this.sessions.length > 0))
      },
      isTwoStagedLot () {
        return this.lot.lotType === LOT_TYPES.TWO_STAGED_OFFERS
      },
      isPrivateRequestOffersLot () {
        return this.lot.lotType === LOT_TYPES.PRIVATE_REQUEST_OFFERS
      },
      isPublicRequestOffersLot () {
        return this.lot.lotType === LOT_TYPES.PUBLIC_REQUEST_OFFERS
      }
    },
    watch: {
      loading (newValue) {
        this.isLoading = newValue
      }
    },
    methods: {
      ...mapActions(LOT_SESSIONS_MANAGEMENT.NAMESPACE, {
        deleteSessionAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.DELETE_SESSION
      }),
      deleteSession: async function (sessionId) {
        try {
          this.isLoading = true
          await this.deleteSessionAction({
            sessionId,
            lotId: this.lot.lotId,
            lotType: this.lot.lotType
          })
          this.isLoading = false
          this.edited()
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      openAddSessionDialog: function (sessionType) {
        this.addSessionDialog.sessionType = sessionType
        this.addSessionDialog.canViewStartPremiums = this.canViewStartPremiumsForCreating
        this.addSessionDialog.lot = this.lot
        this.addSessionDialog.callback = this.created
        this.addSessionDialog.show = true
      },
      openEditSessionDialog: function (sessionId, sessionType, readonly) {
        this.editSessionDialog.sessionId = sessionId
        this.editSessionDialog.sessionType = sessionType
        this.editSessionDialog.canViewStartPremiums = this.canViewStartPremiumsForEditing
        this.editSessionDialog.lot = this.lot
        this.editSessionDialog.callback = this.edited
        this.editSessionDialog.readonly = readonly
        this.editSessionDialog.show = true
      },
      openConfirmationBeforDelete: function (sessionId) {
        this.confirmDialog.warning = true
        this.confirmDialog.additional = sessionId
        this.confirmDialog.text = this.$t('Сессия будет удалена. Продолжить?')
        this.confirmDialog.callback = this.deleteSession
        this.confirmDialog.show = true
      },
      edited: function () {
        this.$emit('change')
      },
      created: function () {
        this.$emit('change')
      }
    }
  }
</script>
